import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import ActiveUsersModal, { ActiveUser } from './ActiveUsersModal';

interface ActiveUsersBadgeProps {
  userRole: string;
  isAdmin: boolean;
  currentCantiereId?: number | string | null;
  currentCantiereName?: string | null;
  currentView?: string;
  selectedObjectName?: string | null;
}

const ActiveUsersBadge: React.FC<ActiveUsersBadgeProps> = ({
  userRole,
  isAdmin,
  currentCantiereId,
  currentCantiereName,
  currentView,
  selectedObjectName,
}) => {
  const [activeCount, setActiveCount] = useState<number>(1);
  const [users, setUsers] = useState<ActiveUser[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const canViewDetails = isAdmin || userRole === 'operator';

  // Compute action description based on current view/context
  const computeActionDescription = useCallback(() => {
    if (currentView === 'objectDashboard' && selectedObjectName) {
      return `Oggetto: ${selectedObjectName}`;
    }
    if (currentView === 'cantiereBrowser' && (currentCantiereName || currentCantiereId)) {
      return `Cantiere: ${currentCantiereName || currentCantiereId}`;
    }
    if (currentView === 'adminCantieri') return 'Gestione Cantieri';
    if (currentView === 'adminClusters') return 'Gestione Cluster';
    if (currentView === 'adminUsers') return 'Gestione Utenti';
    if (currentView === 'manageIngestion') return 'Gestione Ingestion';
    if (currentView === 'adminConfig') return 'Configurazione Sistema';
    if (currentView === 'documentation') return 'Documentazione';
    return 'Dashboard generale';
  }, [currentView, currentCantiereName, currentCantiereId, selectedObjectName]);

  // Fetch active sessions from backend
  const fetchActiveSessions = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await api.get('/user/active-sessions');
      if (typeof data.active_count === 'number') {
        setActiveCount(data.active_count);
      }
      if (Array.isArray(data.users)) {
        setUsers(data.users);
      }
    } catch {
      // Silently ignore background polling errors
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Send heartbeat signal
  const sendHeartbeat = useCallback(async () => {
    try {
      const payload: { cantiere_id?: number | null; action: string } = {
        action: computeActionDescription(),
      };
      if (currentCantiereId) {
        payload.cantiere_id = Number(currentCantiereId);
      }
      await api.post('/user/heartbeat', payload);
    } catch {
      // Silently ignore background heartbeat errors
    }
  }, [computeActionDescription, currentCantiereId]);

  // Initial fetch and heartbeat
  useEffect(() => {
    sendHeartbeat();
    fetchActiveSessions();
  }, [sendHeartbeat, fetchActiveSessions]);

  // Polling intervals
  useEffect(() => {
    // Poll sessions every 45 seconds (or 15s if modal is open)
    const sessionInterval = setInterval(() => {
      fetchActiveSessions();
    }, isModalOpen ? 15000 : 45000);

    // Send heartbeat every 45 seconds
    const heartbeatInterval = setInterval(() => {
      sendHeartbeat();
    }, 45000);

    return () => {
      clearInterval(sessionInterval);
      clearInterval(heartbeatInterval);
    };
  }, [fetchActiveSessions, sendHeartbeat, isModalOpen]);

  const handleBadgeClick = () => {
    if (canViewDetails) {
      fetchActiveSessions();
      setIsModalOpen(true);
    }
  };

  const labelText = activeCount === 1 ? '1 online' : `${activeCount} online`;

  return (
    <>
      <div
        onClick={handleBadgeClick}
        title={
          canViewDetails
            ? `Utenti attivi: ${activeCount}. Clicca per vedere chi è connesso e le attività.`
            : `Utenti attivi: ${activeCount}.`
        }
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '4px 10px',
          borderRadius: '16px',
          backgroundColor: 'rgba(255, 255, 255, 0.18)',
          border: '1px solid rgba(255, 255, 255, 0.35)',
          color: '#ffffff',
          fontSize: '0.82rem',
          fontWeight: 500,
          cursor: canViewDetails ? 'pointer' : 'default',
          transition: 'all 0.2s ease',
          userSelect: 'none',
        }}
        onMouseEnter={(e) => {
          if (canViewDetails) {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.28)';
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.6)';
          }
        }}
        onMouseLeave={(e) => {
          if (canViewDetails) {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.18)';
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.35)';
          }
        }}
      >
        <span
          style={{
            display: 'inline-block',
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: '#00e676',
            boxShadow: '0 0 6px #00e676',
          }}
        />
        <span>{labelText}</span>
        {canViewDetails && (
          <i
            className="fas fa-chevron-down"
            style={{ fontSize: '0.65rem', opacity: 0.8, marginLeft: '2px' }}
          />
        )}
      </div>

      {canViewDetails && (
        <ActiveUsersModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          users={users}
          activeCount={activeCount}
          isLoading={isLoading}
          onRefresh={fetchActiveSessions}
        />
      )}
    </>
  );
};

export default ActiveUsersBadge;
